/**
 * Pure state storage for variables.
 * Manages static, dynamic, and user-editable variables.
 */

import { parseFrontmatter } from '../../shared/index.js';

export interface VariablesResult {
  static: Record<string, string>;
  dynamic: Record<string, string>;
}

export class VariableStore {
  private _staticVariables: Record<string, string> = {};
  private _dynamicVariables: Record<string, string> = {};
  private _userVariables: Record<string, string> = {};

  /** Static variables from frontmatter (no @ prefix) */
  get staticVariables(): Record<string, string> {
    return { ...this._staticVariables };
  }

  /** Dynamic variables from frontmatter (@ prefixed) */
  get dynamicVariables(): Record<string, string> {
    return { ...this._dynamicVariables };
  }

  /** Merged variables with user edits applied */
  get userVariables(): Record<string, string> {
    return { ...this._userVariables };
  }

  /**
   * Set variables from parsed frontmatter.
   */
  setVariables(staticVars: Record<string, string>, dynamicVars: Record<string, string>): void {
    this._staticVariables = { ...staticVars };
    this._dynamicVariables = { ...dynamicVars };
    this._userVariables = { ...staticVars, ...dynamicVars };
  }

  /**
   * Update a user variable value (from the global variables form).
   */
  setUserVariable(varName: string, value: string): void {
    this._userVariables[varName] = value;
  }

  /**
   * Get a user variable value.
   */
  getUserVariable(varName: string): string | undefined {
    return this._userVariables[varName];
  }

  /**
   * Extract variables from frontmatter content.
   * Uses shared parseFrontmatter to avoid duplicating parsing logic.
   */
  extractVariablesFromContent(content: string): VariablesResult {
    const { variables } = parseFrontmatter(content);

    const staticVars: Record<string, string> = {};
    const dynamicVars: Record<string, string> = {};

    for (const v of variables) {
      if (v.isDynamic) {
        dynamicVars[v.name] = v.value;
      } else {
        staticVars[v.name] = v.value;
      }
    }

    return { static: staticVars, dynamic: dynamicVars };
  }

  /**
   * Reset all variable state.
   */
  reset(): void {
    this._staticVariables = {};
    this._dynamicVariables = {};
    this._userVariables = {};
  }
}
